import { Component, ElementRef, HostListener, inject, input, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { isNumber } from 'lodash-es';
import { Subscription } from 'rxjs';
import { CoverageOption } from '../../interfaces/coverage-option.model';
import { Coverage } from '../../interfaces/coverage.model';
import { Feedback } from '../../interfaces/feedback.model';
import { ValidationError } from '../../interfaces/validation-error.model';
import { CoverageType } from '../enums/coverage-type.enum';
import { CurrencyPipe } from '../pipes/currency.pipe';
import { CoverageOptionMap, ReferenceDataSet, ReferenceDataValue } from '../services/reference-data.types';
import { formatCurrency, formatPercentage } from '../utils/formatting';
import { BaseWidgetComponent } from './base.widget.component';

@Component({
  selector: 'app-base-coverage.widget.component',
  imports: [],
  template: '', // No template defined
  providers: [CurrencyPipe],
  styles: [],
})
export abstract class BaseCoverageWidgetComponent extends BaseWidgetComponent {
  customerIndex = input.required<number>();
  coverageIndex = input.required<number>();
  coverage = input.required<Coverage>();

  @ViewChild('sumInsuredInput', { static: false }) sumInsuredInputRef!: ElementRef<HTMLInputElement>;

  protected _formBuilder: FormBuilder = inject(FormBuilder);
  protected _formSumInsuredSubscription?: Subscription = new Subscription();
  override _dataSubscription: Subscription = new Subscription();

  protected form!: FormGroup;
  protected _showRulesAlertBanner: boolean = false;
  errorMessage: string = '';
  sicknessOnly: string = '';
  hasUnderwritingTerms: boolean = false;

  override ngOnInit(): void {
    super.ngOnInit();

    this._initialiseForm();
    this._initialiseSumInsuredBaseline();

    // Subscribe to data updates
    this._dataSubscription = this._baseDataService.data$.subscribe(data => {
      if (data) {
        this.sharedPolicyData = structuredClone(data);
        // Perform any additional updates here
        this._patchFormFromData();
      }
    });

    this._formSumInsuredSubscription = this.form.get('sumInsured')?.valueChanges.subscribe(value => {
      if (this.coverage() && typeof value === 'string') {
        parseFloat(value.replace(/[$,]/g, ''));
      }
    });

    if (this._dataSubscription && this._formSumInsuredSubscription) {
      this.addSubscription(this._dataSubscription);
      this.addSubscription(this._formSumInsuredSubscription);
    }
  }

  get inputMaxLength() {
    if (this.coverage().sumInsuredOriginal) {
      return (this.coverage().sumInsuredOriginal! / 100).toString().length + 3; // +1 for if user wants to use cents rounded to tens
    }
    return 50;
  }

  get sicknessRefDS(): ReferenceDataSet | undefined {
    if (this.coverage().options!.filter((option: CoverageOption) => option.name === 'INCT01').length > 0) {
      if (this.sicknessOnly === 'SICK') {
        return this._referenceDataService.getReferenceDataSet('incomeCoverType');
      }
    }
    return this._referenceDataService.getReferenceDataSet('sicknessOnlyIncomeCover');
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const inputElement = this.sumInsuredInputRef; // Reference to your input element

    // Check if the input is empty
    const isInputEmpty = !inputElement?.nativeElement.value;

    // Check if the click was outside the input element
    if (isInputEmpty && inputElement && !inputElement.nativeElement.contains(target)) {
      event.preventDefault(); // prevent clicking outside
      event.stopPropagation();
    }
  }

  // Call this when initializing or resetting data
  protected _initialiseSumInsuredBaseline() {
    if (this.sharedPolicyData && this.coverageIndex()) {
      this.previousSumInsured = this.sharedPolicyData?.coverages![this.coverageIndex()]?.sumInsured?.amount ?? null;
    }
  }

  protected _initialiseForm(): void {
    this.form = this._formBuilder.group({
      paymentFrequency: [''],
      sumInsured: new FormControl(this.coverage().sumInsured?.amount! / 100 || '', [Validators.required]),
      premium: [''],
      premiumReviewPeriod: [''],
      indexation: [''],
      smoker: [''],
      occupationDefinition: [''],
      occupationClass: [''],
      waitPeriod: [''],
      paymentTerm: [''],
      coverTerm: [''],
      incomeCoverType: [''],
      tpd: [''],
      lifetimeRewardPercent01: [''],
      lifetimeRewardCode01: [''],
      legacyDiscount: [''],
      sicknessOnlyIncomeCover: [''],
      loeType: [''],
      gio: [false],
      lifetimeReward: [false],
      reductionInWaitingPeriod: [false],
      dependentLeaveWithoutPay: [false],
      mortgageLuxury: [false],
      underwritingTerms: [false],
      lifeBuyback: [false],
      traumaBuyback: [false],
      optTrauma: [false],
      mentalHealthRestriction: [false],
      incomeCoverExtra: [false],
      extraOption: [false],
    });
  }

  /**
   * Updates the form controls with the latest values from the shared policy data and coverage.
   * This keeps the form in sync with backend data for the selected customer and coverage.
   *
   * @returns {void}
   */
  // eslint-disable-next-line complexity
  protected _patchFormFromData() {
    if (this.coverage()) {
      const options = this.mapCoverageOptions(this.coverage().options);

      this.form.patchValue({
        sumInsured: formatCurrency(this.coverage().sumInsured?.amount! / 100) ?? '',
        premium: formatCurrency(this.coverage().premium?.amount! / 100) ?? '',
        premiumReviewPeriod: this.coverage().premiumReviewPeriod ?? '',
        indexation: this.coverage().indexation ?? '',
        smoker: this.coverage().smoker ?? '',
        occupationClass: this.coverage().occupation?.class ?? '',
        waitPeriod: this.coverage().waitPeriod ?? '',
        paymentTerm: this.coverage().paymentTerm ?? '',
        coverTerm: this.coverage().coverTerm ?? '',

        incomeCoverType: options['INCT01']?.value ?? '',
        occupationDefinition: options['OCDE01']?.value ?? '',
        tpd: options['OCDE01']?.value ?? '',
        lifetimeRewardPercent01: formatPercentage(options['LTRP01']?.value ?? ''),
        lifetimeRewardCode01: options['LTRC02']?.value ?? '',
        legacyDiscount: options['LEDI01']?.value ?? '',
        sicknessOnlyIncomeCover:
          options['INCT01']?.value && options['SOIC01']?.value === 'SICK' ? options['INCT01']?.value : '',
        loeType: options['LOTY01']?.value ?? '',

        underwritingTerms:
          this.coverage().loadingPercentage != null ||
          this.coverage().loadingPermillie != null ||
          this.coverage().loadingPartialTerm != null ||
          this.coverage().exclusions?.length! > 0,

        // Using interpretCoverageValue to handle 'Y', 'N', null/undefined
        gio: this.interpretCoverageValue(options['GIO01']?.value!),
        lifetimeReward: this.interpretCoverageValue(options['LIRE01']?.value!),
        mortgageLuxury: this.interpretCoverageValue(options['MODE01']?.value!),
        reductionInWaitingPeriod: this.interpretCoverageValue(options['REWP01']?.value!),
        dependentLeaveWithoutPay: this.interpretCoverageValue(options['DERE01']?.value!),
        lifeBuyback: this.interpretCoverageValue(options['LIBU01']?.value!),
        traumaBuyback: this.interpretCoverageValue(options['TRBU01']?.value!),
        optTrauma: this.interpretCoverageValue(options['OCTR01']?.value!),
        mentalHealthRestriction: this.interpretCoverageValue(options['MEHR01']?.value!),
        incomeCoverExtra: this.interpretCoverageValue(options['INC01']?.value!),
        extraOption: this.interpretCoverageValue(options['LOEE01']?.value!),
      });
      this.hasUnderwritingTerms = this.form.get('underwritingTerms')?.value;
      this.sicknessOnly = this.coverage().options!.find((option: CoverageOption) => option.name === 'SOIC01')?.value!;
      this.previousSumInsured = this.coverage().sumInsured?.amount ?? 0;
    }
  }

  /**
   * Called when the sum insured input loses focus.
   * This function formats the currency, validates the input, detects changes,
   * and triggers a premium calculation if required.
   *
   * @param {HTMLInputElement} inputEl - The HTML input element for sum insured.
   * @returns {void}
   */
  onSumInsuredBlur(inputEl: HTMLInputElement): void {
    this.formatCurrency(inputEl); // Format the currency
    const valueStr = inputEl.value.replace(/\$/g, '').replace(/,/g, '');
    const inputValue = parseFloat(valueStr);

    // Reset previous error
    if (!isNumber(inputValue)) {
      this.errorMessage = 'Invalid input for sum insured.';
      return;
    }

    if (this.errorMessage.length > 0) {
      return;
    }

    const currentSumInsured = inputValue * 100; // covert to cents

    // Check if the value has changed
    if (this.previousSumInsured !== null && currentSumInsured === this.previousSumInsured) {
      // No change, do not trigger calculation
      return;
    }

    // Update previous value
    this.previousSumInsured = currentSumInsured;

    // Validate against original amount
    const originalAmount = this.sharedPolicyData?.coverages![this.coverageIndex()].sumInsuredOriginal ?? 0;
    const valueIsZero = currentSumInsured === 0;
    const nonInjuryValueIsTooLow =
      currentSumInsured < 100000 &&
      this.sharedPolicyData?.coverages![this.coverageIndex()]?.type !== CoverageType.SPECIFIC_INJURY;
    const valueIsLargerThanOriginal = currentSumInsured > originalAmount;

    if (valueIsZero || nonInjuryValueIsTooLow || valueIsLargerThanOriginal) {
      return;
    }

    // If valid, trigger the premium calculation
    this.sharedPolicyData!.coverages![this.coverageIndex()].sumInsured!.amount = currentSumInsured;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this._baseTriggerPremiumCalculation().catch(error => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      this._utilsService.reAttemptIfTokenExpiry(() => this._baseTriggerPremiumCalculation()).catch((reAttemptError: string) => {
        this.showError(
          `Error performing premium calculation: An error has occurred, please call the Chubb Life team on 0508 464 999 for assistance.`,
        );
      });
    });

    // Reset stored sumInsured after premium calculation
    this._initialiseSumInsuredBaseline();
  }

  /**
   * Extracts and returns error messages related to the currently selected customer and coverage
   * from the shared policy data evaluation response.
   *
   * @returns {string[]} An array of error messages specific to the relevant customer and coverage.
   */
  get alertBannerErrors(): string[] {
    const validationErrors = this.sharedPolicyData?.evaluationResponse?.errors ?? [];
    const errorMessages: string[] = [];

    validationErrors.forEach((error: ValidationError) => {
      const loweredPaths = error.path.map(p => p.toLowerCase());

      const hasCustomer = loweredPaths.some(p => p.includes(`customers[${this.customerIndex()}]`));
      const hasCoverage = loweredPaths.some(p => p.includes(`coverages[${this.coverageIndex()}]`));

      if (hasCustomer && hasCoverage) {
        errorMessages.push(error.message); // Correctly push message into errorMessages
      }
    });

    return errorMessages; // Return the collected string messages
  }

  /**
   * Formats the given input element's value as New Zealand currency (NZD).
   * If the input is not a valid number, sets a custom validation error.
   *
   * @param {HTMLInputElement} inputElement - The input element to format.
   * @returns {void}
   */
  formatCurrency(inputElement: HTMLInputElement): void {
    const value = inputElement.value.replace(/,/g, '');
    if (isNaN(Number(value))) {
      inputElement.setCustomValidity('Please enter a valid number');
      return;
    }
    inputElement.setCustomValidity('');
    inputElement.value = new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 2,
    }).format(Number(value));
  }

  protected mapCoverageOptions(value: CoverageOption[] | null | undefined): CoverageOptionMap {
    const optionMap: CoverageOptionMap = {};
    if (!value) {
      // handle null, undefined, or non-array inputs
      return optionMap;
    }
    for (const i of value) {
      optionMap[i.name ?? ''] = this._mapCoverageValue(i);
    }
    return optionMap;
  }

  private _mapCoverageValue(value: CoverageOption): CoverageOption {
    return value;
  }

  protected getRefDSByCoverageCode(coverageCode: string, refDataSetName: string): ReferenceDataSet | undefined {
    return this._referenceDataService.getReferenceDataSetByCoverageCode(coverageCode, refDataSetName);
  }

  protected getOptions(datasetname: string): ReferenceDataValue[] {
    const options = this.getRefDSByCoverageCode(this.coverage().code!, datasetname);
    return options || []; // Fallback to an empty array if undefined
  }

  private readonly interpretCoverageValue = (value?: string): boolean => {
    return value === 'Y';
  };

  /**
   * Checks if there is a validation error present for a specific property
   * on a coverage item belonging to a particular customer.
   *
   * @param {number} cusIndex - The index of the customer to check.
   * @param {number} coverIndex - The index of the coverage to check.
   * @param {string} property - The property name within the coverage to check for errors.
   * @returns {boolean} True if a validation error exists for the specified property; otherwise, false.
   */
  hasErrorForCoverage(cusIndex: number, coverIndex: number, property: string): boolean {
    if (!this.sharedPolicyData?.evaluationResponse?.errors) {
      return false; // No errors to check against
    }

    const customerPathToCheck = `customers[${cusIndex}]`.toLowerCase();
    const coveragePathToCheck = `coverages[${coverIndex}].${property}`.toLowerCase();

    const conditionMet = this.sharedPolicyData.evaluationResponse.errors.some(
      (error: ValidationError) =>
        error.path.some(p => p.toLowerCase().startsWith(customerPathToCheck)) &&
        error.path.some(p => p.toLowerCase() === coveragePathToCheck),
    );

    return conditionMet;
  }

  /**
   * Triggered on field change
   * Allows only numeric input (including decimal point) for the sum insured input field.
   * Prevents entry of non-numeric characters except for navigation and editing keys.
   *
   * @param {KeyboardEvent} event - The keyboard event triggered by the input.
   * @returns {void}
   */
  allowOnlyNumbers(event: KeyboardEvent): void {
    const isTab = event.key === 'Tab'; // detect Tab key

    // Check if the current value is empty or null
    const currentValue = (event.target as HTMLInputElement).value;

    if ((currentValue === '' || currentValue == null) && isTab) {
      // Prevent tab from moving away if value is null or empty
      event.preventDefault();
      return;
    }

    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];
    const decimalKey = '.';

    if (
      allowedKeys.includes(event.key) ||
      /^\d$/.test(event.key) ||
      (event.key === decimalKey && !(event.target as HTMLInputElement).value.includes('.'))
    ) {
      // Allow key
    } else {
      event.preventDefault();
    }
  }

  preventNonNumericPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text') ?? '';
    if (!/^\d*\.?\d*$/.test(pastedText)) {
      event.preventDefault();
    }
  }

  onFocusSelect(inputElement: HTMLInputElement): void {
    // Remove currency formatting
    this.removeCurrencyFormat(inputElement);
    // Select all text
    inputElement.select();
  }

  removeCurrencyFormat(inputElement: HTMLInputElement): void {
    const value = inputElement.value.replace(/[$,]/g, '');
    inputElement.value = value;
  }

  /**
   * Validates the current sum insured value against the original sum insured amount.
   * Sets the appropriate error message based on New Zealand insurance business rules.
   *
   * @returns {void}
   */
  validateNewAgainstOriginal(): void {
    const control = this.form.get('sumInsured');
    const value = control?.value;
    const original = this.coverage().sumInsuredOriginal! / 100;

    if (value && !isNaN(+value)) {
      const numericValue = parseFloat(value.replace(/[$,]/g, ''));

      if (numericValue === 0) {
        this.errorMessage = 'Sum insured cannot be zero.';
      } else if (
        numericValue < 1000 &&
        this.sharedPolicyData?.coverages![this.coverageIndex()]?.type !== CoverageType.SPECIFIC_INJURY
      ) {
        this.errorMessage = 'The minimum sum insured is $1,000.00.';
      } else if (numericValue > original) {
        this.errorMessage = 'Sum insured must be less than or equal to original sum insured';
      } else {
        this.errorMessage = '';
      }
    }
  }

  getRulesFeedbacks(): Feedback[] {
    if (this.sharedPolicyData?.rulesFeedbacks && this.sharedPolicyData.rulesFeedbacks.length > 0) {
      this.triggerRulesFeedbackAlert();
      // Return all feedbacks
      return this.sharedPolicyData.rulesFeedbacks;
    }
    // Return an empty array if no feedbacks are present
    return [];
  }

  protected triggerRulesFeedbackAlert() {
    this._showRulesAlertBanner = true;
  }

  protected clearRulesFeedback() {
    if (this.sharedPolicyData?.rulesFeedbacks) {
      this.sharedPolicyData.rulesFeedbacks = [];
    }
  }

  /**
   * Retrieves the rules validation error message for a specific field within a customer or coverage collection.
   *
   * @param {('customers' | 'coverages')} collection - The collection to check ('customers' or 'coverages').
   * @param {number} index - The index of the item within the collection.
   * @param {string} field - The field name to check for a rules error.
   * @returns {string | null} The error message if present; otherwise, null.
   */
  protected getRulesErrorForPath(collection: 'customers' | 'coverages', index: number, field: string): string | null {
    const path = `${collection}[${index}].${field}`;
    const error = this.sharedPolicyData?.evaluationResponse?.errors?.find((err: ValidationError) =>
      err.path.includes(path),
    );

    return error ? error.message : null;
  }

  protected hasRulesError(collection: 'customers' | 'coverages', index: number, field: string): boolean {
    return !!this.getRulesErrorForPath(collection, index, field);
  }
}
